import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import ReactQueryBuilder, {
  ValueEditor as DefaultValueEditor,
  ActionElement as DefaultActionElement,
  Rule as DefaultRule,
  findRule,
} from 'react-querybuilder';
import Select from 'react-select';
import { withTheme } from 'common/styling/theme';
import { injectIntl } from 'react-intl';
import { FormMessageHOC } from 'common/HOCs';
import { reduxForm } from 'redux-form';
import BaseTextField from '../form-fields/BaseTextField';
import { staticStyles, getDynamicStyles } from './style';

const IN_OPERATOR = { name: 'in', label: 'IN' };

const IN_EXCLUDED_FIELDS = new Set([
  'days_since_registration',
  'baserequest__days_since_first_deposit',
  'baserequest__deposit__amount_in_USD',
  'baserequest__withdraw__amount_in_USD',
  'baserequest__deposit__created',
  'baserequest__withdraw__created',
]);

const defaultQuery = {
  rules: [],
  combinator: 'and',
  not: false,
};

const QueryValueEditor = props => {
  const { className, field, handleOnChange, operator, value, values } = props;

  if (operator !== IN_OPERATOR.name || !values?.length) {
    return <DefaultValueEditor {...props} />;
  }

  const options = values.map(item => ({ value: item.name, label: item.label }));
  let selectedValues = [];
  if (Array.isArray(value)) {
    selectedValues = value;
  } else if (value) {
    selectedValues = [value];
  }

  return (
    <Select
      className={`${className} QueryBuilder__multi-value`}
      classNamePrefix="QueryBuilderSelect"
      closeMenuOnSelect={false}
      id={`${field}-in-values`}
      isMulti
      isSearchable
      onChange={selectedOptions => handleOnChange(selectedOptions.map(item => item.value))}
      options={options}
      value={options.filter(option => selectedValues.includes(option.value))}
    />
  );
};

QueryValueEditor.propTypes = {
  className: PropTypes.string,
  field: PropTypes.string.isRequired,
  handleOnChange: PropTypes.func.isRequired,
  operator: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.array]),
  values: PropTypes.array,
};

QueryValueEditor.defaultProps = {
  className: '',
  value: '',
  values: [],
};

const RuleActionWithComment = props => {
  const { context } = props;

  return (
    <>
      <div className="QueryBuilder__rule-comment">
        <BaseTextField
          aria-label={context.commentPlaceholder}
          autoComplete="off"
          name={`rule-comment-${context.ruleId}`}
          onChange={event => context.handleCommentChange(event.target.value)}
          placeholder={context.commentPlaceholder}
          title={context.comment || undefined}
          type="text"
          value={context.comment}
        />
      </div>
      <DefaultActionElement {...props} />
    </>
  );
};

RuleActionWithComment.propTypes = {
  context: PropTypes.shape({
    comment: PropTypes.string,
    commentPlaceholder: PropTypes.string.isRequired,
    handleCommentChange: PropTypes.func.isRequired,
    ruleId: PropTypes.string.isRequired,
  }).isRequired,
};

const RuleWithComment = props => {
  const { context, id, schema } = props;
  const rule = findRule(id, context.query);
  const ruleContext = {
    ...context,
    comment: rule?.comment ?? '',
    handleCommentChange: comment => schema.onPropChange('comment', comment, id),
    ruleId: id,
  };
  const ruleSchema = {
    ...schema,
    controls: {
      ...schema.controls,
      removeRuleAction: RuleActionWithComment,
    },
  };

  return <DefaultRule {...props} context={ruleContext} schema={ruleSchema} />;
};

RuleWithComment.propTypes = {
  context: PropTypes.shape({
    commentPlaceholder: PropTypes.string.isRequired,
    query: PropTypes.object.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    controls: PropTypes.object.isRequired,
    onPropChange: PropTypes.func.isRequired,
  }).isRequired,
};

const QueryBuilder = ({ input, filters, initialQuery, onChangeQuery, theme, intl }) => {
  filters = filters.map(filter => ({ ...filter, id: filter.id.toString() }));
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [query, setQuery] = useState(initialQuery ?? defaultQuery);

  const combinators = [
    { name: 'and', label: intl.formatMessage({ id: 'justAnd' }) },
    { name: 'or', label: intl.formatMessage({ id: 'justOr' }) },
  ];

  const operators = [
    { name: '=', label: '=' },
    { name: '<', label: '<' },
    { name: '>', label: '>' },
    { name: '<=', label: '<=' },
    { name: '>=', label: '>=' },
  ];

  const translations = {
    fields: {
      title: 'Fields',
    },
    operators: {
      title: 'Operators',
    },
    value: {
      title: 'Value',
    },
    removeRule: {
      label: 'x',
      title: intl.formatMessage({ id: 'justRemoveRule' }),
    },
    removeGroup: {
      label: 'x',
      title: intl.formatMessage({ id: 'justRemoveGroup' }),
    },
    addRule: {
      label: 'Add rule',
      title: intl.formatMessage({ id: 'justAddRule' }),
    },
    addGroup: {
      label: 'Add group',
      title: intl.formatMessage({ id: 'justAddGroup' }),
    },
    combinators: {
      title: 'Combinators',
    },
    notToggle: {
      label: 'not',
      title: intl.formatMessage({ id: 'invertGroup' }),
    },
  };

  const handleQueryChange = q => {
    setQuery(q);

    if (onChangeQuery) {
      onChangeQuery(q);
    }

    if (input && Object.prototype.hasOwnProperty.call(input, 'onChange')) {
      input.onChange(q);
    }
  };

  const fields = useMemo(
    () => Object.values(filters).map(item => ({ name: item.field, label: item.nice_name })),
    [filters]
  );

  const getValueEditorType = (field, operator) => {
    const filterItem = filters.find(filter => filter.field === field);
    if (operator === IN_OPERATOR.name) {
      return filterItem?.options?.length && filterItem.options[0] !== 'None' ? 'select' : 'text';
    }

    switch (filterItem?.type) {
      case 'bool':
        return 'checkbox';
      case 'date':
        return 'text';
      case 'decimal':
        return filterItem?.options[0] === 'None' ? 'text' : 'select';
      case 'str':
        return filterItem?.options[0] === 'None' ? 'text' : 'select';

      default:
        return 'select';
    }
  };

  const getInputType = (field, operator) => {
    const filterItem = filters.find(filter => filter.field === field);
    if (operator === IN_OPERATOR.name) {
      return 'text';
    }

    switch (filterItem?.type) {
      case 'date':
        return 'date';

      case 'decimal':
        return 'number';

      default:
        return 'text';
    }
  };

  const getValues = field => {
    const filterItem = filters.find(filter => filter.field === field);
    if ((filterItem?.type === 'str' || filterItem?.type === 'decimal') && filterItem?.options[0] !== 'None') {
      if (filterItem.is_countries_filter) {
        return filterItem?.options ? filterItem?.options : [];
      }
      return filterItem?.options?.map(item => ({ name: item, label: item })) ?? [];
    }
    return [];
  };

  const getOperators = field => {
    const filterItem = filters.find(filter => filter.field === field);
    const supportsIn = !IN_EXCLUDED_FIELDS.has(field);

    switch (filterItem?.type) {
      case 'bool':
        return [{ name: '=', label: '=' }];

      case 'str':
        return [
          { name: '=', label: '=' },
          { name: 'startwith', label: intl.formatMessage({ id: 'justStartsWith' }) },
          { name: 'contains', label: intl.formatMessage({ id: 'justContains' }) },
          ...(supportsIn ? [IN_OPERATOR] : []),
        ];

      case 'decimal':
        return supportsIn ? [...operators, IN_OPERATOR] : operators;

      default:
        return null;
    }
  };

  const getDefaultValue = rule => {
    if (rule.operator === IN_OPERATOR.name) {
      return [];
    }

    const values = getValues(rule.field);
    if (values.length) {
      return values[0].name;
    }
    if (getValueEditorType(rule.field, rule.operator) === 'checkbox') {
      return false;
    }
    return '';
  };

  return (
    <div className="QueryBuilder__wrapper">
      <ReactQueryBuilder
        query={query}
        fields={fields}
        translations={translations}
        combinators={combinators}
        operators={operators}
        onQueryChange={handleQueryChange}
        getValues={getValues}
        getValueEditorType={getValueEditorType}
        getInputType={getInputType}
        getOperators={getOperators}
        getDefaultValue={getDefaultValue}
        controlElements={{ valueEditor: QueryValueEditor, rule: RuleWithComment }}
        resetOnOperatorChange
        context={{ commentPlaceholder: intl.formatMessage({ id: 'justComment' }), query }}
        showNotToggle
      />
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

QueryBuilder.propTypes = {
  input: PropTypes.object,
  intl: PropTypes.object.isRequired,
  onChangeQuery: PropTypes.func.isRequired,
  filters: PropTypes.array,
  initialQuery: PropTypes.object,
  theme: PropTypes.object,
};

QueryBuilder.defaultProps = {
  input: {},
  initialQuery: null,
  filters: [],
  theme: {},
};

export default compose(
  withTheme(),
  FormMessageHOC,
  reduxForm({
    form: 'QueryBuilder',
    onSubmitSuccess: (result, dispatch, props) => {
      props.showFormSuccessMessage(props.successMessageId);
    },
  }),
  connect(state => ({
    countries: state.admin.payments.countries,
  }))
)(injectIntl(QueryBuilder));
export { QueryBuilder };
